/**
 * KAN-865: строка CRM одного клиента для панели карточки канбан-доски
 * (GET /api/v1/crm/clients/<id>/ -> { row, attention, stages, attributes }).
 *
 * Кеш хранится в форме доски CRM (CrmBoard) под ключом
 * [...CRM_BOARD_QUERY_KEY, 'client', id]: правка ячейки (useCrmRowEdit) и
 * действия строки (useCrmRowActions, withCrmRowAction) работают с ним так же,
 * как с таблицей. Требует ли клиент внимания, говорит поле attention ответа;
 * старый backend его не шлёт: тогда строка с причиной или задачей.
 *
 * Строка внимания лежит и в attention, и в days: панель показывает первую
 * (внимание). Действие оптимистично снимает её из attention и проставляет исход
 * строке в days (withCrmRowAction), поэтому до нового ответа панель показывает
 * отработанную строку с уже выставленным исходом: не пустеет, и кнопки
 * "Успех"/"Отказ" после исхода не возвращаются.
 */
import { useQuery } from '@tanstack/react-query';
import { crmApi } from '@/services/api/crm';
import { CRM_BOARD_QUERY_KEY } from '@/services/api/queryKeys';
import { crmTableRows, type CrmTableRow } from '@/lib/crmTable';
import type { CrmBoard, CrmRow } from '@/types/crm';
import type { CrmClientDetail } from '@/types/crmPipeline';

export const crmClientRowKey = (clientId: string | null) => [...CRM_BOARD_QUERY_KEY, 'client', clientId] as const;

/** Старый backend без поля attention: строка внимания та, у которой есть причина или задача. */
function looksLikeAttention(row: CrmRow): boolean {
  return !!row.reason?.trim() || !!row.task?.text?.trim();
}

/** Детали клиента в форме доски CRM. */
export function clientDetailToBoard({ row, attention, stages, attributes }: CrmClientDetail): CrmBoard {
  const pending = attention ?? looksLikeAttention(row);
  return {
    attention: pending ? [row] : [],
    days: [{ date: '', rows: [row] }],
    stages: stages ?? [],
    attributes: attributes ?? [],
  };
}

export function useCrmClientRow(clientId: string | null) {
  const query = useQuery({
    queryKey: crmClientRowKey(clientId),
    queryFn: async () => clientDetailToBoard(await crmApi.getClientRow(clientId as string)),
    enabled: clientId !== null,
    retry: false,
  });
  const item: CrmTableRow | null = query.data ? (crmTableRows(query.data)[0] ?? null) : null;
  return { ...query, item, queryKey: crmClientRowKey(clientId) };
}
